import L, { PointTuple } from 'leaflet';
import { Marker, Tooltip } from 'react-leaflet';
import React, { useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'styled-components';

import { useTranslation } from 'helpers/hooks';

import { isMarker, Marker as MarkerModel, MarkerStatus } from '../../../types/marker';
import { FilterRadius } from '../FilterRadius';
import { AppState } from '../../../store/interfaces';
import { setSelectedObject } from '../../../store/map/actions';

const tooltipOffset: PointTuple = [0, -20];

interface Icon {
  iconUrl: string;
  iconSize: [number, number];
}

const getIcon = ({ isActive, isSelectedMarker, theme, marker, hasNewPin }): Icon => {
  return isActive
    ? {
        iconUrl:
          isSelectedMarker && marker.status === MarkerStatus.inactive
            ? '/assets/map/pin_selected.svg'
            : theme.name === 'light'
            ? '/assets/map/pin_active_light.svg'
            : '/assets/map/pin_active_dark.svg',
        iconSize: hasNewPin ? [25, 40] : isSelectedMarker ? [30, 45] : [25, 40],
      }
    : {
        iconUrl:
          theme.name === 'light'
            ? '/assets/map/pin_inactive_light.svg'
            : '/assets/map/pin_inactive_dark.svg',
        iconSize: [25, 40],
      };
};

interface SearchPinProps {
  isSelectedMarker: boolean;
  isShowFilterRadius: boolean;
  marker: MarkerModel;
  selectMarker(marker: MarkerModel): void;
  isActive: boolean;
  hasNewPin: boolean;
}

const getHint = (status: number) => {
  if (status === MarkerStatus.inactive) {
    return 'map.pins.inactivePin';
  }

  return 'map.pins.activePin';
};

const SearchPin: React.FC<SearchPinProps> = React.memo(function ({
  isSelectedMarker,
  isShowFilterRadius,
  marker,
  selectMarker,
  isActive,
  hasNewPin,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const icon = getIcon({ isActive, isSelectedMarker, theme, hasNewPin, marker });

  return (
    <React.Fragment
      key={`${marker.lat}-${
        marker.lng
      }-${isSelectedMarker}-${isShowFilterRadius}-${icon.toString()}`}
    >
      {isShowFilterRadius && <FilterRadius lat={marker.lat} lng={marker.lng} />}
      <Marker
        icon={L.icon(icon)}
        position={{
          lat: +marker.lat,
          lng: +marker.lng,
        }}
        zIndexOffset={isSelectedMarker ? 4000 : 1000}
        eventHandlers={{
          click: () => {
            selectMarker(marker);
          },
        }}
      >
        {isSelectedMarker && (
          <Tooltip direction="top" opacity={1} permanent offset={tooltipOffset}>
            {t(getHint(marker.status))}
          </Tooltip>
        )}
      </Marker>
    </React.Fragment>
  );
});

export const SearchPins: React.FC = React.memo(function () {
  const selectedObject = useSelector((app: AppState) => app.map.selectedObject);
  const newPin = useSelector((app: AppState) => app.map.newPin);
  const markers = useSelector((app: AppState) => app.map.markers.list);
  const showInactivePins = useSelector((app: AppState) => app.map.layers.inactivePins);

  const dispatch = useDispatch();
  const selectMarker = useCallback((marker) => dispatch(setSelectedObject(marker)), []);

  if (markers.length === 0) {
    return null;
  }

  return (
    <>
      {markers.map((marker: MarkerModel) => {
        const isSelectedMarker =
          selectedObject !== null && isMarker(selectedObject) && marker.id === selectedObject.id;

        const isActive = isSelectedMarker || marker.status !== 0;

        if (!showInactivePins && !isActive) {
          return null;
        }

        const isShowFilterRadius = marker.status !== 0 || isSelectedMarker;

        return (
          <SearchPin
            key={`${marker.lat}-${marker.lng}-${isSelectedMarker}-${isShowFilterRadius}`}
            marker={marker}
            isShowFilterRadius={isShowFilterRadius}
            isSelectedMarker={isSelectedMarker}
            selectMarker={selectMarker}
            isActive={isActive}
            hasNewPin={!!newPin}
          />
        );
      })}
    </>
  );
});
